import GlobalTour from "@/components/GuidedTour";
import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { useActivity, GLOBAL_SCOPE } from "@/context/ActivityContext";
import SyncStatus from "@/components/SyncStatus";
import Modal from "@/components/Modal";
import { useOffline } from "@/context/OfflineContext";
import {
  LayoutDashboard, ScanBarcode, HandCoins, Boxes, Package, Users, Wallet,
  Receipt, Layers, ShieldCheck, UserCog, LogOut, Menu, ChevronDown, Store,
  Truck, Briefcase, KeyRound, CalendarDays, Settings as SettingsIcon, FileText, ShoppingBag, Tags, BookOpen, BarChart3, Warehouse, Send, Ban, WifiOff, UserCircle, HelpCircle, ChevronRight, ChevronLeft, Mail, MessageCircle,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: "*" },
  { to: "/reports", label: "Rapports & Stats", icon: BarChart3, roles: ["owner", "admin", "manager", "accountant"] },
  { to: "/seller-report", label: "Rapport vendeurs", icon: BarChart3, roles: ["owner", "admin", "manager", "accountant"] },
  { to: "/cancellations", label: "Journal des annulations", icon: Ban, roles: ["owner", "admin", "manager", "accountant"] },
  { to: "/authorizations", label: "Autorisations", icon: KeyRound, roles: ["owner", "admin", "manager"] },
  { to: "/locations", label: "Emplacements", icon: Warehouse, roles: ["owner", "admin", "manager"] },
  { to: "/supplies", label: "Approvisionnements", icon: Send, roles: ["owner", "admin", "manager", "storekeeper", "cashier", "seller"] },
  { to: "/returns", label: "Retours clients", icon: FileText, roles: ["owner", "admin", "manager", "cashier"], mod: "returns" },
  { to: "/pos", label: "Ventes (POS)", icon: ScanBarcode, roles: ["owner", "admin", "manager", "cashier"], mod: "pos" },
  { to: "/receivables", label: "Créances", icon: HandCoins, roles: ["owner", "admin", "manager", "cashier", "accountant"], mod: "receivables" },
  { to: "/quotes", label: "Devis / Proforma", icon: FileText, roles: ["owner", "admin", "manager", "cashier"], mod: "quotes" },
  { to: "/stock", label: "Stock", icon: Boxes, roles: ["owner", "admin", "manager", "storekeeper"], mod: "stock" },
  { to: "/products", label: "Catalogue", icon: Package, roles: ["owner", "admin", "manager", "storekeeper"], mod: "products" },
  { to: "/categories", label: "Catégories", icon: Tags, roles: ["owner", "admin", "manager", "storekeeper"], mod: "products" },
  { to: "/parties", label: "Clients & Fournisseurs", icon: Users, roles: ["owner", "admin", "manager", "cashier"], mod: "parties" },
  { to: "/purchases", label: "Achats / Fournisseurs", icon: ShoppingBag, roles: ["owner", "admin", "manager", "storekeeper", "accountant"], mod: "purchases" },
  { to: "/cash", label: "Caisse", icon: Wallet, roles: ["owner", "admin", "manager", "cashier"], mod: "cash" },
  { to: "/expenses", label: "Dépenses", icon: Receipt, roles: ["owner", "admin", "manager", "accountant"], mod: "expenses" },
  { to: "/hr", label: "Ressources humaines", icon: Briefcase, roles: ["owner", "admin", "accountant"], mod: "hr" },
  { to: "/fleet", label: "Logistique / Camion", icon: Truck, roles: ["owner", "admin", "manager", "storekeeper"], mod: "fleet" },
  { to: "/rentals", label: "Locations & actifs", icon: KeyRound, roles: ["owner", "admin", "manager", "cashier"], mod: "rentals" },
  { to: "/deliveries", label: "Calendrier livraisons", icon: CalendarDays, roles: ["owner", "admin", "manager", "storekeeper", "cashier"], mod: "deliveries" },
];

const ROLE_LABEL = {
  owner: "Propriétaire", admin: "Administrateur", manager: "Responsable",
  cashier: "Caissier", seller: "Vendeur", storekeeper: "Magasinier", accountant: "Comptable", auditor: "Auditeur",
};

function allowed(item, role, modules) {
  if (item.roles === "*" || item.roles.includes(role)) return true;
  // A3 : un droit-module accordé (allowed_modules, reflété dans `modules`) rend le module visible même hors rôle par défaut
  if (item.mod && Array.isArray(modules) && modules.includes(item.mod)) return true;
  return false;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { activities, current, setCurrent, modules, assignedLocations, settings, multiSeller, locationsVisible } = useActivity();
  const [open, setOpen] = useState(false);
  const [actMenu, setActMenu] = useState(false);
  const [profileMenu, setProfileMenu] = useState(false);
  const [submenu, setSubmenu] = useState(null); // null | "admin" | "help"
  const [showHelp, setShowHelp] = useState(false);
  const menuRef = useRef(null);
  const nav = useNavigate();
  const loc = useLocation();
  const navStackRef = useRef([]);
  const navPrevRef = useRef(loc.pathname + loc.search);
  const navSkipRef = useRef(false);
  const { online } = useOffline();
  const [toReceive, setToReceive] = useState(0);
  useEffect(() => {
    api.get("/supplies/to-receive-count").then((r) => setToReceive(r.data.count || 0)).catch(() => {});
  }, [loc.pathname]);
  const offlineCapable = ["/pos", "/cash"].includes(loc.pathname);
  const GLOBAL_ALLOWED = ["/", "/reports", "/activities", "/users", "/audit", "/settings", "/locations"];
  const items = NAV.filter((i) => allowed(i, user.role, modules) && (!i.mod || modules === null || modules.includes(i.mod)))
    .filter((i) => !current?.global || GLOBAL_ALLOWED.includes(i.to))
    .filter((i) => !(current && !current.global && current.stock_mode !== "advanced" && i.to === "/supplies"))
    .filter((i) => !(i.to === "/locations" && locationsVisible !== true))
    .filter((i) => !(i.to === "/seller-report" && multiSeller !== true));
  useEffect(() => {
    if (current?.global && !GLOBAL_ALLOWED.includes(loc.pathname)) {
      toast.message("Sélectionnez une activité pour accéder à ce module.");
      nav("/", { replace: true });
    }
    // eslint-disable-next-line
  }, [current?.global, loc.pathname]);
  // Historique interne de navigation : enregistre chaque écran visité (indépendant de push/replace).
  useEffect(() => {
    const curKey = loc.pathname + loc.search;
    if (navSkipRef.current) { navSkipRef.current = false; navPrevRef.current = curKey; return; }
    if (curKey !== navPrevRef.current) {
      navStackRef.current.push(navPrevRef.current);
      if (navStackRef.current.length > 50) navStackRef.current.shift();
      navPrevRef.current = curKey;
    }
    // eslint-disable-next-line
  }, [loc.pathname, loc.search]);
  const activeActs = activities.filter((a) => a.status !== "archived");

  // Mode caisse plein écran (sans barre latérale) : pour un vendeur/caissier partout,
  // et pour TOUT profil lorsqu'il ouvre la caisse (POS). Purement visuel — droits inchangés.
  const sellerProfile = !user.is_owner && ["cashier", "seller"].includes(user.role);
  const cashMode = sellerProfile || loc.pathname === "/pos";
  const actionItems = (() => {
    // Vendeur/caissier : TOUS ses modules autorisés (hors tableau de bord), libellés courts, 1 ou 2 lignes, sans « Plus »
    if (sellerProfile) {
      const SHORT = { "/pos": "Vendre", "/parties": "Clients", "/deliveries": "Livraisons", "/quotes": "Devis", "/returns": "Retours", "/receivables": "Créances" };
      const ordered = [...items.filter((i) => i.to === "/pos"), ...items.filter((i) => i.to !== "/pos" && i.to !== "/")];
      return ordered.map((i) => ({ to: i.to, label: SHORT[i.to] || i.label, icon: i.icon }));
    }
    // Autres profils dans le POS : Vendre + Tableau de bord (la navigation complète est dans le tableau de bord)
    return [{ to: "/pos", label: "Vendre", icon: ScanBarcode }, { to: "/", label: "Tableau de bord", icon: LayoutDashboard }];
  })();

  const doLogout = async () => { await logout(); nav("/login"); };
  // Retour global : revient à l'écran réellement consulté juste avant, via l'historique interne.
  // Aucun écran précédent (entrée directe / deep-link) → tableau de bord. Jamais hors de Biashara.
  const goBack = () => {
    const stack = navStackRef.current;
    if (stack.length > 0) {
      const target = stack.pop();
      navSkipRef.current = true;
      navPrevRef.current = target;
      nav(target);
    } else {
      nav("/");
    }
  };

  // Menu Profil : compact avec sous-menus déroulants (Administration, Aide) — ne bloque PAS le défilement du tableau de bord.
  const role = user.role;
  const isSeller = role === "seller";
  const canSettings = user.is_owner || ["owner", "admin"].includes(role);
  const canUsers = user.is_owner || ["owner", "admin"].includes(role);
  const canAudit = user.is_owner || ["owner", "admin", "auditor", "accountant"].includes(role);
  const canActivities = user.is_owner || ["owner", "admin", "manager"].includes(role);
  const hasAdmin = canSettings || canUsers || canAudit || canActivities;
  const supportEmail = (settings?.company_email || "").trim();
  const supportPhone = (settings?.company_phone || "").trim();
  const waHref = supportPhone ? `https://wa.me/${supportPhone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent("Bonjour, j'ai besoin d'aide sur Biashara.")}` : null;
  const closeMenu = () => { setProfileMenu(false); setSubmenu(null); };
  const go = (to) => { closeMenu(); nav(to); };
  const menuItemCls = "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-[#334155] hover:bg-slate-50 transition-colors";
  const sectionCls = "text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold px-3 pt-2 pb-1";
  useEffect(() => { closeMenu(); /* eslint-disable-next-line */ }, [loc.pathname]);
  useEffect(() => {
    if (!profileMenu) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu(); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [profileMenu]);

  return (
    <div className="h-screen overflow-hidden flex bg-[#F8FAFC]">
      {/* Sidebar */}
      {!cashMode && (<aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-gradient-to-b from-[#16324F] via-[#153050] to-[#0E2135] text-white flex flex-col transition-transform duration-200 shadow-[0_10px_40px_-10px_rgba(0,0,0,.5)] ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#0E7490] flex items-center justify-center font-bold font-head text-lg shadow-[0_8px_20px_-6px_rgba(37,99,235,.7)]">B</div>
          <div>
            <div className="font-bold font-head text-lg leading-none tracking-tight">Biashara</div>
            {(() => { const sub = user?.role === "superadmin" ? "ERP multi-activité · SaaS" : (current && !current.global ? current.name : (current?.global ? "Vue globale" : (settings?.org_name || "ERP multi-activité · SaaS"))); return (
            <div className="text-[11px] text-white/55 mt-0.5 truncate max-w-[170px]" title={sub} data-testid="brand-subtitle">{sub}</div>); })()}
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
          <div className="section-label">Navigation</div>
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink key={it.to} to={it.to} end={it.to === "/"} onClick={() => setOpen(false)}
                data-testid={`nav-${it.to.replace("/", "") || "dashboard"}`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}>
                <span className="nav-ico"><Icon className="w-[16px] h-[16px]" /></span>
                <span className="truncate">{it.label}</span>
                {it.to === "/supplies" && toReceive > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#1D4ED8] text-white text-[11px] font-bold" data-testid="nav-supplies-badge">{toReceive}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </aside>)}

      {!cashMode && open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0]">
          <div className="flex items-center gap-3 px-4 md:px-6 h-16">
            {!cashMode && (
            <button className="lg:hidden btn-ghost !p-2" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu className="w-5 h-5" />
            </button>
            )}
            {loc.pathname !== "/" && (
              <button onClick={goBack} data-testid="header-back-btn" aria-label="Retour à l'écran précédent" title="Retour"
                className="btn-ghost !p-2 shrink-0 rounded-lg hover:bg-slate-100">
                <ChevronLeft className="w-5 h-5 text-[#16324F]" />
              </button>
            )}
            {/* Activity switcher */}
            <div className="relative">
              <button onClick={() => setActMenu((v) => !v)} data-testid="activity-switcher"
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white hover:bg-slate-50 text-sm font-medium transition-colors">
                <Store className="w-4 h-4 text-[#2563EB]" />
                <span className="max-w-[160px] truncate">{current?.name || "Aucune activité"}</span>
                <ChevronDown className="w-4 h-4 text-[#94A3B8]" />
              </button>
              {actMenu && (
                <div className="absolute left-0 mt-1 w-64 card p-1.5 z-30">
                  {(user.is_owner || ["owner", "admin", "manager"].includes(user.role)) && (<>
                    <button data-testid="activity-option-global"
                      onClick={() => { setCurrent(GLOBAL_SCOPE); setActMenu(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-slate-50 ${current?.global ? "bg-[#EFF6FF] text-[#2563EB] font-semibold" : ""}`}>
                      Vue globale (toutes les activités)
                    </button>
                    <div className="my-1 border-t border-[#E2E8F0]" />
                  </>)}
                  {activeActs.length === 0 && <div className="px-3 py-2 text-sm text-[#64748B]">Aucune activité</div>}
                  {activeActs.map((a) => (
                    <button key={a.id} data-testid={`activity-option-${a.id}`}
                      onClick={() => { setCurrent(a); setActMenu(false); }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-slate-50 ${current?.id === a.id ? "bg-[#EFF6FF] text-[#2563EB] font-semibold" : ""}`}>
                      {a.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-3" data-testid="header-identity">
              <div className="text-right hidden sm:block leading-tight">
                <div className="text-sm font-semibold text-[#16324F]" data-testid="header-user-name">{user.name}</div>
                <div className="text-[11px] text-[#64748B] flex items-center gap-1.5 justify-end flex-wrap max-w-[280px]">
                  <span data-testid="header-user-role">{ROLE_LABEL[user.role] || user.role}</span>
                  {current && !current.global && (<>
                    <span className="text-[#CBD5E1]">·</span>
                    <span className="inline-flex items-center gap-1" data-testid="header-activity"><Store className="w-3 h-3 text-[#2563EB]" />{current.name}</span>
                  </>)}
                  {(assignedLocations || []).length > 0 && (<>
                    <span className="text-[#CBD5E1]">·</span>
                    <span className="inline-flex items-center gap-1" data-testid="header-user-location"><Warehouse className="w-3 h-3 text-[#0E7490]" />{assignedLocations.join(", ")}</span>
                  </>)}
                </div>
              </div>
              <div className="relative" data-testid="profile-menu" ref={menuRef}>
                <button onClick={() => setProfileMenu((v) => !v)} data-testid="profile-menu-btn" aria-label="Menu du compte" aria-haspopup="true" aria-expanded={profileMenu}
                  className="w-9 h-9 rounded-full bg-gradient-to-br from-[#16324F] to-[#2563EB] text-white flex items-center justify-center text-sm font-semibold ring-2 ring-white shadow-sm cursor-pointer hover:ring-[#2563EB] focus:outline-none focus:ring-[#2563EB] transition-all"
                  title={`${user.name} · ${ROLE_LABEL[user.role] || user.role}`}>
                  {user.name?.[0]?.toUpperCase()}
                </button>
                {profileMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-[#E2E8F0] shadow-xl p-1.5 z-50 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="profile-dropdown">
                    {submenu === null && (<>
                      <div className="px-3 py-2">
                        <div className="text-sm font-semibold text-[#16324F] truncate" data-testid="profile-name">{user.name}</div>
                        <div className="text-[11px] text-[#64748B]">{ROLE_LABEL[user.role] || user.role}</div>
                      </div>
                      <div className="my-1 border-t border-[#E2E8F0]" aria-hidden="true" />
                      <button onClick={() => go("/account")} data-testid="menu-account" className={menuItemCls}><UserCircle className="w-4 h-4 text-[#2563EB]" /> Mon compte</button>
                      {isSeller ? (<>
                        <button onClick={() => go("/guide")} data-testid="menu-guide" className={menuItemCls}><BookOpen className="w-4 h-4 text-[#64748B]" /> Guide de démarrage</button>
                        <button onClick={() => go("/formation")} data-testid="menu-formation" className={menuItemCls}><BookOpen className="w-4 h-4 text-[#64748B]" /> Formation</button>
                      </>) : (<>
                        {hasAdmin && <button onClick={() => setSubmenu("admin")} data-testid="menu-admin-group" className={menuItemCls + " justify-between"}><span className="flex items-center gap-2.5"><SettingsIcon className="w-4 h-4 text-[#64748B]" /> Administration</span><ChevronRight className="w-4 h-4 text-[#94A3B8]" /></button>}
                        <button onClick={() => setSubmenu("help")} data-testid="menu-help-group" className={menuItemCls + " justify-between"}><span className="flex items-center gap-2.5"><HelpCircle className="w-4 h-4 text-[#64748B]" /> Aide et formation</span><ChevronRight className="w-4 h-4 text-[#94A3B8]" /></button>
                      </>)}
                      <div className="my-1 border-t border-[#E2E8F0]" aria-hidden="true" />
                      <button onClick={() => { closeMenu(); doLogout(); }} data-testid="logout-btn" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-[#B91C1C] hover:bg-red-50 transition-colors"><LogOut className="w-4 h-4" /> Déconnexion</button>
                    </>)}
                    {submenu === "admin" && (<>
                      <button onClick={() => setSubmenu(null)} data-testid="submenu-back" className={menuItemCls + " font-medium"}><ChevronLeft className="w-4 h-4 text-[#64748B]" /> Retour</button>
                      <div className={sectionCls} data-testid="menu-section-admin">Administration</div>
                      {canSettings && <button onClick={() => go("/settings")} data-testid="menu-settings" className={menuItemCls}><SettingsIcon className="w-4 h-4 text-[#64748B]" /> Paramètres de l'entreprise</button>}
                      {canActivities && <button onClick={() => go("/activities")} data-testid="menu-activities" className={menuItemCls}><Layers className="w-4 h-4 text-[#64748B]" /> Activités</button>}
                      {canUsers && <button onClick={() => go("/users")} data-testid="menu-users" className={menuItemCls}><UserCog className="w-4 h-4 text-[#64748B]" /> Utilisateurs</button>}
                      {canAudit && <button onClick={() => go("/audit")} data-testid="menu-audit" className={menuItemCls}><ShieldCheck className="w-4 h-4 text-[#64748B]" /> Audit de sécurité</button>}
                    </>)}
                    {submenu === "help" && (<>
                      <button onClick={() => setSubmenu(null)} data-testid="submenu-back" className={menuItemCls + " font-medium"}><ChevronLeft className="w-4 h-4 text-[#64748B]" /> Retour</button>
                      <div className={sectionCls} data-testid="menu-section-help">Aide et formation</div>
                      <button onClick={() => go("/guide")} data-testid="menu-guide" className={menuItemCls}><BookOpen className="w-4 h-4 text-[#64748B]" /> Guide de démarrage</button>
                      <button onClick={() => go("/formation")} data-testid="menu-formation" className={menuItemCls}><BookOpen className="w-4 h-4 text-[#64748B]" /> Formation</button>
                      <button onClick={() => { closeMenu(); setShowHelp(true); }} data-testid="menu-assistance" className={menuItemCls}><HelpCircle className="w-4 h-4 text-[#64748B]" /> Assistance</button>
                    </>)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        <SyncStatus actions={cashMode ? actionItems : null} />
        <main className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 lg:p-8 max-w-[1600px] w-full mx-auto animate-fade-up">
          {!online && !offlineCapable && (
            <div className="mb-5 flex items-center gap-3 rounded-xl bg-[#16324F] text-white px-4 py-3 text-sm shadow-md" data-testid="offline-notice">
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-white/10 shrink-0"><WifiOff className="w-4 h-4 text-[#93C5FD]" /></span>
              <span>Données indisponibles hors connexion. Reconnectez-vous pour actualiser cette page. La caisse et les ventes restent disponibles hors-ligne.</span>
            </div>
          )}
          {activeActs.length === 0 && !["/activities", "/settings", "/users", "/audit", "/account"].includes(loc.pathname) ? (
            <div className="max-w-lg mx-auto text-center py-16 md:py-24" data-testid="no-activity-empty">
              <div className="w-14 h-14 rounded-2xl bg-[#EFF6FF] text-[#2563EB] flex items-center justify-center mx-auto mb-4"><Store className="w-7 h-7" /></div>
              <h2 className="text-2xl font-bold font-head text-[#16324F]">Vous n'avez actuellement aucune activité</h2>
              <p className="text-sm text-[#64748B] mt-2">Créez une activité pour accéder à la caisse, au stock, aux ventes et aux rapports.</p>
              <button onClick={() => nav("/activities")} className="btn-primary mt-6 mx-auto" data-testid="empty-create-activity-btn"><Store className="w-4 h-4" /> Créer une activité</button>
            </div>
          ) : <Outlet />}
        </main>
      </div>
      <GlobalTour />
      {showHelp && (
        <Modal open onClose={() => setShowHelp(false)} title="Assistance" testId="assistance-modal"
          footer={<button className="btn-outline" onClick={() => setShowHelp(false)}>Fermer</button>}>
          <div className="space-y-4 text-sm text-[#475569]">
            <div className="flex flex-col gap-2">
              <button className="btn-outline justify-start" onClick={() => { setShowHelp(false); nav("/guide"); }} data-testid="assistance-guide"><BookOpen className="w-4 h-4" /> Guide de démarrage</button>
              <button className="btn-outline justify-start" onClick={() => { setShowHelp(false); nav("/formation"); }} data-testid="assistance-formation"><BookOpen className="w-4 h-4" /> Formation</button>
            </div>
            {(waHref || supportEmail) ? (
              <div>
                <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] font-semibold mb-2">Contacter le support</div>
                <div className="flex flex-col gap-2">
                  {waHref && <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-outline justify-start !text-[#15803D]" data-testid="assistance-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp {supportPhone}</a>}
                  {supportEmail && <a href={`mailto:${supportEmail}?subject=${encodeURIComponent("Assistance Biashara")}`} className="btn-outline justify-start !text-[#2563EB]" data-testid="assistance-email"><Mail className="w-4 h-4" /> {supportEmail}</a>}
                </div>
              </div>
            ) : (
              <p className="text-xs text-[#94A3B8]">Ajoutez un téléphone et un e-mail d'entreprise dans Paramètres pour activer le contact direct du support.</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
